// src/hooks/useContacts.ts

import { db } from '../firebase/config';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface Contact {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
  groups?: string[];
}

export function useContacts() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const contactsRef = collection(db, 'users', user.uid, 'contacts');
    const unsubscribe = onSnapshot(contactsRef, (snapshot) => {
      const docs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as Contact[];
      setContacts(docs);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [user]);

  async function addContact(contact: Omit<Contact, 'id'>) {
    if (!user) return;
    const contactsRef = collection(db, 'users', user.uid, 'contacts');
    await addDoc(contactsRef, contact);
  }

  async function updateContact(contactId: string, updatedData: Partial<Contact>) {
    if (!user) return;
    const docRef = doc(db, 'users', user.uid, 'contacts', contactId);
    await updateDoc(docRef, updatedData);
  }

  async function deleteContact(contactId: string) {
    if (!user) return;
    const docRef = doc(db, 'users', user.uid, 'contacts', contactId);
    await deleteDoc(docRef);
  }

  return { contacts, loading, addContact, updateContact, deleteContact };
}
